import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Upload, Database, Lock, TrendUp, Crown, CheckCircle } from '@phosphor-icons/react';

export function UploadChoice() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const canUploadPrivate = user?.tier !== 'free';
  const remainingPublicAnalyses = (user?.publicAnalysesLimit || 0) - (user?.publicAnalysesUsed || 0);

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <span className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase block mb-4">
            New analysis
          </span>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight mb-2">
            How are you starting?
          </h1>
          <p className="text-muted-foreground">
            Upload your own data or analyze a public dataset from GEO or recount3.
          </p>
        </div>

        {/* Cards — gap-px bento grid */}
        <div className="grid md:grid-cols-2 gap-px bg-border border border-border rounded-2xl overflow-hidden mb-8">

          {/* Private Data */}
          <div className="flex flex-col p-8 bg-background">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-9 h-9 bg-teal-50 border border-teal-100 rounded-lg flex items-center justify-center shrink-0">
                <Upload className="w-4 h-4 text-teal-600" />
              </div>
              <div>
                <h3 className="text-foreground font-semibold leading-tight">Upload private data</h3>
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground/40">
                  {canUploadPrivate ? 'Included in your plan' : 'Professional plan required'}
                </span>
              </div>
            </div>
            <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
              Upload your own gene expression files for confidential analysis. Your data is encrypted and auto-deleted after completion.
            </p>
            {!canUploadPrivate && (
              <div className="flex items-start gap-2.5 mb-5 pb-5 border-b border-border">
                <Lock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground">Private uploads require a Professional plan.</p>
              </div>
            )}
            <ul className="space-y-2.5 mb-8 flex-1">
              {[
                'Your data stays secure and private',
                'Process proprietary research datasets',
                'Auto-deleted after analysis',
              ].map(f => (
                <li key={f} className="flex items-center gap-2.5 text-sm">
                  <CheckCircle className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
                  <span className="text-muted-foreground">{f}</span>
                </li>
              ))}
            </ul>
            {canUploadPrivate ? (
              <Button
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium"
                onClick={() => navigate('/upload/private')}
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload my data
              </Button>
            ) : (
              <Button
                variant="outline"
                className="w-full border-border hover:bg-muted/30"
                onClick={() => navigate('/checkout?plan=professional')}
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to upload
              </Button>
            )}
          </div>

          {/* Public Data */}
          <div className="flex flex-col p-8 bg-background">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-9 h-9 bg-teal-50 border border-teal-100 rounded-lg flex items-center justify-center shrink-0">
                <Database className="w-4 h-4 text-teal-600" />
              </div>
              <div>
                <h3 className="text-foreground font-semibold leading-tight">Browse public datasets</h3>
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground/40">Free tier</span>
              </div>
            </div>
            <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
              Analyze publicly available datasets from GEO, recount3, and other repositories. Pre-validated and quality-checked.
            </p>
            <div className="mb-5 pb-5 border-b border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Monthly usage</span>
                <span className="text-sm font-mono text-foreground">
                  {user?.publicAnalysesUsed} / {user?.publicAnalysesLimit}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-1.5">
                <div
                  className="bg-teal-500 h-1.5 rounded-full transition-all"
                  style={{ width: `${((user?.publicAnalysesUsed || 0) / (user?.publicAnalysesLimit || 1)) * 100}%` }}
                />
              </div>
              {user?.tier !== 'free' && (
                <p className="text-xs text-teal-600 mt-1.5 flex items-center gap-1">
                  <TrendUp className="w-3 h-3" />
                  Unlimited with your plan
                </p>
              )}
            </div>
            <ul className="space-y-2.5 mb-8 flex-1">
              {[
                'Access 1,000+ curated datasets',
                'Pre-validated and quality checked',
                'Great for testing and exploration',
              ].map(f => (
                <li key={f} className="flex items-center gap-2.5 text-sm">
                  <CheckCircle className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
                  <span className="text-muted-foreground">{f}</span>
                </li>
              ))}
            </ul>
            <Button
              variant="outline"
              className="w-full border-border hover:bg-muted/30"
              onClick={() => navigate('/public-data')}
              disabled={remainingPublicAnalyses <= 0 && user?.tier === 'free'}
            >
              {remainingPublicAnalyses <= 0 && user?.tier === 'free'
                ? 'Monthly limit reached'
                : 'Browse public datasets'
              }
            </Button>
          </div>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed pt-2">
          <strong className="text-foreground font-medium">Not sure?</strong> Use public datasets to explore the platform and compare methods — no files needed. Use private upload when you have your own RNA-seq data to keep confidential.
        </p>
      </div>
    </div>
  );
}